import { PWAManager, ServerAPI, CameraManager, WebRTCManager, UIManager } from "./managers";
import { extractVideoStats, formatBitrate } from "./utils/statsDisplay";
import { CONFIG, ELEMENT_IDS } from "./config";
import type { AppMode, ConnectionState, QRData } from "./types";

// Simple poller implementation since utils/Poller might not exist
class Poller {
  private intervalId: number | null = null;
  private isRunning: boolean = false;

  constructor(
    private pollFn: () => Promise<boolean>,
    private stopCondition: () => boolean,
    private useAdaptive: boolean = false
  ) {}

  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.poll();
  }

  private async poll(): Promise<void> {
    if (!this.isRunning) return;

    try {
      if (this.stopCondition()) {
        this.stop();
        return;
      }

      const shouldContinue = await this.pollFn();
      if (!shouldContinue || this.stopCondition()) {
        this.stop();
        return;
      }
    } catch (error) {
      // Silent error handling
    }

    if (this.isRunning) {
      this.intervalId = window.setTimeout(() => this.poll(), 1000);
    }
  }

  stop(): void {
    this.isRunning = false;
    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
  }
}

export class HairCamApp {
  private pwaManager: PWAManager;
  private serverAPI: ServerAPI;
  private cameraManager: CameraManager;
  private webrtcManager: WebRTCManager | null = null;
  private uiManager: UIManager;

  // No more quality management - just use best available resolution

  private connectionId: string | null = null;
  private currentMode: AppMode | null = null;
  private connectionState: ConnectionState = "idle";

  private candidatePoller: Poller | null = null;
  private minimalPoller: Poller | null = null;
  private connectionTimeout: number | null = null;
  private hasCleanedUpConnection: boolean = false;
  private statsInterval: number | null = null;

  constructor() {
    this.pwaManager = new PWAManager();
    this.serverAPI = new ServerAPI();
    this.cameraManager = new CameraManager();
    this.uiManager = new UIManager();

    this.initializeApp();
    this.setupEventListeners();
  }

  private initializeApp(): void {
    if (this.pwaManager.isPWAInstalled()) {
      this.uiManager.showModeSelection();
    } else {
      this.uiManager.showInstallScreen();
    }
  }

  private setupEventListeners(): void {
    // PWA install events
    this.pwaManager.setupEventListeners(
      () => this.uiManager.showModeSelection(),
      () => this.uiManager.showModeSelection()
    );

    // Mode selection events
    document.getElementById(ELEMENT_IDS.VIEWER_BTN)?.addEventListener("click", () => {
      this.startViewerMode();
    });

    document.getElementById(ELEMENT_IDS.CAMERA_BTN)?.addEventListener("click", () => {
      this.startCameraMode();
    });

    // Back button events
    document.getElementById(ELEMENT_IDS.BACK_FROM_VIEWER)?.addEventListener("click", () => {
      this.goBackToModeSelection();
    });

    document.getElementById(ELEMENT_IDS.BACK_FROM_CAMERA)?.addEventListener("click", () => {
      this.goBackToModeSelection();
    });

    // Camera switch event
    document.getElementById(ELEMENT_IDS.SWITCH_CAMERA_BTN)?.addEventListener("click", () => {
      this.switchCamera();
    });
  }

  private async startViewerMode(): Promise<void> {
    try {
      this.currentMode = "viewer";
      this.uiManager.showViewerScreen();
      this.uiManager.exitFullscreenVideo();

      // Create WebRTC manager for viewer
      this.webrtcManager = new WebRTCManager("viewer");

      // Create peer connection with event handlers
      await this.webrtcManager.createPeerConnection(
        (event) => this.handleRemoteTrack(event),
        (state) => this.handleConnectionStateChange(state),
        () => this.handleIceConnectionStateChange()
      );

      // Create offer and wait for ICE gathering
      const offer = await this.webrtcManager.createViewerOffer();
      this.uiManager.updateConnectionStatus("Preparing connection...");
      await this.webrtcManager.waitForICEGathering();

      // Send offer and ICE candidates to server
      const response = await this.serverAPI.createConnection(offer, this.webrtcManager.iceCandidates);

      this.connectionId = response.connectionId;
      this.webrtcManager.clearPendingCandidates();

      // Generate QR code and start polling
      await this.uiManager.generateQRCode(this.connectionId, CONFIG.API_BASE);
      this.startMinimalPolling();
      this.uiManager.updateConnectionStatus("Waiting for camera to connect...");
    } catch (error) {
      this.uiManager.updateConnectionStatus("Error starting viewer mode");
    }
  }

  private async startCameraMode(): Promise<void> {
    try {
      this.currentMode = "camera";
      this.uiManager.showCameraScreen();

      // Start QR scanner
      await this.cameraManager.startQRScanner((qrData) => this.handleQRScan(qrData));
      this.cameraManager.updateSwitchCameraButtonVisibility();
      this.uiManager.updateCameraConnectionStatus("Point camera at QR code");
    } catch (error) {
      this.uiManager.updateCameraConnectionStatus("Error accessing camera");
    }
  }

  private async handleQRScan(qrData: QRData): Promise<void> {
    try {
      this.connectionId = qrData.connectionId;

      // Stop QR scanner
      this.cameraManager.stopQRScanner();

      this.uiManager.updateCameraConnectionStatus("Getting high quality video stream...");

      // Get high-resolution video streaming stream from CameraManager
      const localStream = await this.cameraManager.getVideoStreamingStream();

      this.cameraManager.setupCameraStreamVideo();

      // Create WebRTC manager for camera
      this.webrtcManager = new WebRTCManager("camera");
      await this.webrtcManager.createPeerConnection(
        undefined, // Camera doesn't receive tracks
        (state) => this.handleConnectionStateChange(state),
        () => this.handleIceConnectionStateChange()
      );

      this.uiManager.updateCameraConnectionStatus("Getting connection data...");

      // Get connection data from server
      const connectionData = await this.serverAPI.getConnection(this.connectionId);

      if (connectionData.offer) {
        // Create answer using standard WebRTC
        const answer = await this.webrtcManager.createCameraAnswer(connectionData.offer, localStream);

        // Wait for camera to generate its ICE candidates
        await this.webrtcManager.waitForICEGathering();

        // Send answer AND camera's ICE candidates to server
        await this.serverAPI.sendAnswer(this.connectionId, answer, this.webrtcManager.iceCandidates);

        // Process ICE candidates from viewer
        if (connectionData.iceCandidates?.length > 0) {
          await this.webrtcManager.addIceCandidates(connectionData.iceCandidates);
        }

        this.uiManager.updateCameraConnectionStatus("Connecting...");
      } else {
        throw new Error("No offer found in connection data");
      }
    } catch (error) {
      this.uiManager.updateCameraConnectionStatus("Invalid QR code");
    }
  }

  private async showInitialVideoStats(): Promise<void> {
    if (!this.webrtcManager?.connection || !this.cameraManager.currentStream) {
      return;
    }

    try {
      const stats = await extractVideoStats(
        this.webrtcManager.connection,
        false // Camera mode
      );

      // Display the stats using UIManager
      this.uiManager.updateVideoStats(stats.resolution, stats.bitrate, stats.fps);

      this.uiManager.showVideoStats();
    } catch (error) {
      // Silent error handling
    }
  }

  private async showInitialViewerStats(): Promise<void> {
    if (!this.webrtcManager?.connection) {
      return;
    }

    try {
      const stats = await extractVideoStats(
        this.webrtcManager.connection,
        true // Viewer mode
      );

      // Update only the small resolution indicator for viewer mode
      this.uiManager.updateViewerResolution(stats.resolution.width, stats.resolution.height);
    } catch (error) {
      // Silent error handling
    }
  }

  private handleRemoteTrack(event: RTCTrackEvent): void {
    if (event.streams?.[0]) {
      this.uiManager.setupRemoteVideo(event.streams[0]);
    }
  }

  private handleConnectionStateChange(state: ConnectionState): void {
    this.connectionState = state;

    if (state === "connected") {
      this.clearConnectionTimeout();
      this.uiManager.updateConnectionStatus("Connected!");
      this.uiManager.updateCameraConnectionStatus("Connected!");
      this.handleWebRTCConnected();
    } else if (state === "connecting") {
      this.uiManager.updateConnectionStatus("Connecting...");
      this.uiManager.updateCameraConnectionStatus("Connecting...");
      this.startConnectionTimeout();
    } else if (state === "disconnected") {
      this.clearConnectionTimeout();
      this.uiManager.updateConnectionStatus("Connection lost");
      this.uiManager.updateCameraConnectionStatus("Connection lost");
    } else if (state === "failed") {
      this.clearConnectionTimeout();
      this.uiManager.updateConnectionStatus("Connection failed");
      this.uiManager.updateCameraConnectionStatus("Connection failed");
    }
  }

  private handleIceConnectionStateChange(): void {
    // Additional ICE connection handling if needed
  }

  private async handleWebRTCConnected(): Promise<void> {
    // Stop all polling
    this.stopAllPolling();

    // Start stats display for both camera and viewer modes
    if (this.webrtcManager?.connection) {
      if (this.currentMode === "camera") {
        // Show initial stats and start real-time updates
        this.showInitialVideoStats();
        this.startStatsDisplay();
      } else if (this.currentMode === "viewer") {
        // Show initial stats and start real-time updates for viewer
        this.showInitialViewerStats();
        this.startViewerStatsDisplay();
      }
    }

    // Only viewer handles server cleanup, and only once
    if (this.currentMode === "viewer" && this.connectionId && !this.hasCleanedUpConnection) {
      this.hasCleanedUpConnection = true;
      try {
        await this.serverAPI.deleteConnection(this.connectionId);
      } catch (error) {
        // Silent error handling
      }
    }
  }

  private startMinimalPolling(): void {
    if (this.currentMode !== "viewer" || !this.connectionId) return;

    this.minimalPoller = new Poller(
      async () => {
        if (!this.connectionId || !this.webrtcManager?.connection) return false;

        const data = await this.serverAPI.getConnection(this.connectionId);

        // If we detect an answer from camera, switch to full polling
        if (data.answer && !this.webrtcManager.connection.remoteDescription) {
          this.stopMinimalPolling();
          this.startCandidatePolling();
          return false; // Stop minimal polling
        }

        return true; // Continue polling
      },
      () => this.webrtcManager?.connected || false
    );

    this.minimalPoller.start();
  }

  private startCandidatePolling(): void {
    if (this.currentMode !== "viewer" || !this.connectionId) return;

    this.candidatePoller = new Poller(
      async () => {
        if (!this.connectionId || !this.webrtcManager?.connection) return false;

        const data = await this.serverAPI.getConnection(this.connectionId);

        // Set remote answer if available
        if (data.answer) {
          await this.webrtcManager.setRemoteAnswer(data.answer);
        }

        // Add ICE candidates
        if (data.iceCandidates?.length > 0) {
          await this.webrtcManager.addIceCandidates(data.iceCandidates);
        }

        return true; // Continue polling
      },
      () => this.webrtcManager?.connected || false,
      true // Use adaptive intervals
    );

    this.candidatePoller.start();
  }

  private stopMinimalPolling(): void {
    this.minimalPoller?.stop();
    this.minimalPoller = null;
  }

  private stopCandidatePolling(): void {
    this.candidatePoller?.stop();
    this.candidatePoller = null;
  }

  private stopAllPolling(): void {
    this.stopMinimalPolling();
    this.stopCandidatePolling();
  }

  private startStatsDisplay(): void {
    if (!this.webrtcManager?.connection) return;

    // Update stats every 2 seconds
    this.statsInterval = window.setInterval(async () => {
      try {
        if (!this.webrtcManager?.connection) {
          this.stopStatsDisplay();
          return;
        }

        const stats = await extractVideoStats(
          this.webrtcManager.connection,
          false // Camera mode
        );

        // Update UI with current stats
        this.uiManager.updateVideoStats(stats.resolution, stats.bitrate, stats.fps);
      } catch (error) {
        // Silent error handling
      }
    }, 2000);
  }

  private startViewerStatsDisplay(): void {
    if (!this.webrtcManager?.connection) return;

    // Update stats every 2 seconds for viewer mode
    this.statsInterval = window.setInterval(async () => {
      try {
        if (!this.webrtcManager?.connection) {
          this.stopStatsDisplay();
          return;
        }

        const stats = await extractVideoStats(
          this.webrtcManager.connection,
          true // Viewer mode
        );

        // Update only the small resolution indicator for viewer mode
        this.uiManager.updateViewerResolution(stats.resolution.width, stats.resolution.height);
      } catch (error) {
        // Silent error handling
      }
    }, 2000);
  }

  private stopStatsDisplay(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }
  }

  private startConnectionTimeout(): void {
    this.clearConnectionTimeout();
    this.connectionTimeout = window.setTimeout(() => {
      this.uiManager.updateConnectionStatus("Connection timeout");
      this.uiManager.updateCameraConnectionStatus("Connection timeout");

      this.webrtcManager?.close();
    }, CONFIG.TIMEOUTS.CONNECTION);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  private async switchCamera(): Promise<void> {
    if (!this.cameraManager.hasMultipleCameras) return;

    try {
      // Delegate all camera switching logic to CameraManager
      // Pass peer connection if we're in video streaming mode
      const peerConnection = this.webrtcManager?.connection || undefined;
      await this.cameraManager.switchCamera(peerConnection);
    } catch (error) {
      // Silent error handling
    }
  }

  private goBackToModeSelection(): void {
    window.location.reload();
  }
}
